import { Button } from "@solomon-apps/shared/src/components/ui/button";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { LightBarActionButton } from "@solomon-apps/shared/src/components/Yaas/LightBarActionButton";
import { RetryIcon } from "@solomon-labs/design/components/icons";
import React from "react";

import type { OwnershipSourceKind, VerifySlice } from "../steps";

/**
 * The verify step's action row — the `LightBarActionButton` (which doubles as
 * the verify progress indicator) plus the issuer-scoped "verify directly" link
 * and an inline message.
 *
 * It lives OUTSIDE the measured step stack so it can be anchored to the bottom
 * of the fixed-height frame (see `StepFlowPanel`): the stack measures its
 * natural content height to lock the frame, and this bar rides in the leftover
 * space below it. Rendered only while the verify step is current.
 */
export interface VerifyActionBarProps {
    sourceKind: OwnershipSourceKind | null;
    verify: VerifySlice;
}

export function VerifyActionBar({
    sourceKind,
    verify
}: VerifyActionBarProps): React.ReactElement {
    // The button doubles as the progress indicator: its light-bar tracks the
    // verify flow (idle → pending → success/error). After the 3s "Verification Failed" hold
    // the button becomes a plain, enabled "Retry" — default (idle) styling.
    const buttonStatus = verify.isDirect
        ? "idle"
        : verify.status === "verifying"
          ? "pending"
          : verify.status === "verified"
            ? "success"
            : verify.status === "failed"
              ? verify.retryReady
                  ? "idle"
                  : "error"
              : "idle";
    const messageClassName =
        verify.messageTone === "error"
            ? "text-error-base"
            : verify.messageTone === "success"
              ? "text-success-base"
              : "text-foreground-muted";

    // Reserve width for every label the verify button can show in this context,
    // so it never resizes as the label cycles (Verify Ownership → Verifying… →
    // Verified / Verification Failed).
    const verifyReserveLabels = [
        verify.actionLabel,
        sourceKind === "multisig-squads"
            ? "Connect Member Wallet"
            : "Connect Wallet",
        "Verify Ownership",
        "Verifying...",
        "Verified",
        "Verification Failed"
    ];

    return (
        <div className="flex flex-col">
            <div className="flex flex-wrap items-center gap-8">
                <LightBarActionButton
                    label={verify.actionLabel}
                    status={buttonStatus}
                    disabled={verify.actionDisabled}
                    onClick={verify.onVerify}
                    reserveLabels={verifyReserveLabels}
                    icon={
                        verify.status === "failed" && verify.retryReady ? (
                            <RetryIcon className="h-4 w-4" />
                        ) : undefined
                    }
                    className="whitespace-nowrap"
                />
                <Button
                    type="button"
                    variant="transparent"
                    size="md"
                    interaction="underline"
                    disabled={verify.directDisabled}
                    onClick={verify.onVerifyDirect}
                    className="px-0"
                >
                    {verify.isDirect
                        ? "Direct Verification Selected"
                        : verify.directLabel}
                </Button>
                {verify.message && (
                    <Text variant="label" className={messageClassName}>
                        {verify.message}
                    </Text>
                )}
            </div>
        </div>
    );
}
